import { Paper, styled, type TextFieldProps } from '@mui/material';
import {
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import { isValid } from 'date-fns';
import { FC, useState } from 'react';
import { CalendarDay } from '../../utils';

const BorderedPaper = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
}));

export type DatePickerProps = Pick<
  MuiDatePickerProps<Date>,
  'className' | 'name' | 'disableFuture' | 'format' | 'minDate' | 'maxDate'
> &
  Pick<
    TextFieldProps,
    | 'fullWidth'
    | 'size'
    | 'error'
    | 'helperText'
    | 'id'
    | 'required'
    | 'placeholder'
    | 'disabled'
  > & {
    value?: CalendarDay | null;
    onChange: (value: CalendarDay | null) => void;
    clearable?: boolean;
  };

const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  size = 'small',
  format = 'MMM dd, yyyy',
  fullWidth,
  error,
  helperText,
  id,
  required,
  placeholder,
  disabled,
  clearable,
  ...rest
}) => {
  const [open, setOpen] = useState(false);

  const handleChange = (value: Date | null) => {
    if (value === null || isValid(value)) {
      onChange(value ? new CalendarDay(value) : null);
    }
  };

  return (
    <MuiDatePicker
      format={format}
      value={value?.startOfDay() ?? null}
      onChange={handleChange}
      slots={{
        desktopPaper: BorderedPaper,
      }}
      disabled={disabled}
      slotProps={{
        desktopPaper: {
          sx: () => ({
            '& .MuiPickersDay-root.Mui-selected': {
              backgroundColor: 'black',
              color: 'white',
            },
            '& .MuiPickersDay-root:focus.Mui-selected': {
              backgroundColor: 'black',
              color: 'white',
            },
            '& .MuiPickersDay-root.Mui-selected:hover': {
              backgroundColor: 'black',
              color: 'white',
            },
            '& .MuiPickersDay-root.Mui-selected.Mui-focusVisible': {
              backgroundColor: 'black',
              color: 'white',
            },
            '& .MuiPickersDay-root.Mui-selected:focus-visible': {
              backgroundColor: 'black',
              color: 'white',
            },
          }),
        },
        textField: {
          size,
          fullWidth,
          error,
          helperText,
          id,
          required,
          placeholder,
          disabled,
          onClick: () => {
            if (!disabled) {
              setOpen(true);
            }
          },
        },
        openPickerButton: {
          disabled,
        },
        popper: {
          sx: {
            zIndex: 9000,
          },
        },
        field: {
          clearable,
          onClear: () => {
            onChange(null);
          },
        },
      }}
      open={open}
      onOpen={() => {
        if (!disabled) {
          setOpen(true);
        }
      }}
      onClose={() => {
        setOpen(false);
      }}
      {...rest}
    />
  );
};

export default DatePicker;
